import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ApiService, AddressBookEntry } from '../../shared/services/api.service';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConsentFlowService } from '../../shared/services/consent-flow.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-address-book',
  templateUrl: './address-book.component.html',
  styleUrls: ['./address-book.component.css']
})
export class AddressBookComponent implements OnInit {
  displayedColumns: string[] = ['name', 'idNumber', 'lastLookedUpAt', 'availability', 'actions'];
  dataSource = new MatTableDataSource<AddressBookEntry>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  consentRequired = true;
  isCheckingConsent = false;
  isLoadingEntries = false;
  searchTerm = '';
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private dncLoadingByRequestId = new Set<string>();

  constructor(
    private apiService: ApiService,
    private router: Router,
    private snackBar: MatSnackBar,
    private consentFlowService: ConsentFlowService
  ) {}

  ngOnInit(): void {
    void this.ensureConsentThenLoad();
  }

  get isLoadingAddressBook(): boolean {
    return this.isCheckingConsent || this.isLoadingEntries;
  }

  get hasEntries(): boolean {
    return this.dataSource.data.length > 0;
  }

  onSearchChange(value: string): void {
    this.searchTerm = value;
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.loadEntries();
    }, 250);
  }

  loadEntries(showLoader: boolean = true): void {
    if (showLoader) {
      this.isLoadingEntries = true;
    }
    this.apiService.listAddressBook(this.searchTerm).subscribe({
      next: (response) => {
        this.dataSource.data = response.entries;
        setTimeout(() => {
          if (this.paginator) {
            this.dataSource.paginator = this.paginator;
          }
          if (this.sort) {
            this.dataSource.sort = this.sort;
          }
        });
        this.isLoadingEntries = false;
      },
      error: (error) => {
        console.error('Failed to load address book entries:', error);
        const errorMessage = error?.error?.error?.message || 'Failed to load address book entries';
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        this.isLoadingEntries = false;
      }
    });
  }

  private async ensureConsentThenLoad(): Promise<void> {
    this.isCheckingConsent = true;
    try {
      const consentAccepted = await this.consentFlowService.ensureConsentAccepted();
      this.consentRequired = !consentAccepted;
      if (!consentAccepted) {
        this.dataSource.data = [];
        return;
      }

      this.loadEntries();
    } catch (error) {
      console.error('Failed to validate consent status:', error);
      this.snackBar.open('Failed to validate POPIA acknowledgement state', 'Close', { duration: 5000 });
    } finally {
      this.isCheckingConsent = false;
    }
  }

  formatDate(timestamp: string | null): string {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Johannesburg',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    const parts = formatter.formatToParts(date);
    const day = parts.find((p) => p.type === 'day')?.value || '';
    const month = parts.find((p) => p.type === 'month')?.value || '';
    const year = parts.find((p) => p.type === 'year')?.value || '';
    const hour = parts.find((p) => p.type === 'hour')?.value || '';
    const minute = parts.find((p) => p.type === 'minute')?.value || '';
    const second = parts.find((p) => p.type === 'second')?.value || '';
    return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
  }

  formatName(entry: AddressBookEntry): string {
    const trimmed = (entry.name || '').trim();
    return trimmed || 'Name unavailable';
  }

  isMultiSearch(entry: AddressBookEntry): boolean {
    return entry.requestType === 'MULTI_SEARCH' && !!entry.multiSearch;
  }

  getActionIcon(entry: AddressBookEntry): string {
    return this.isMultiSearch(entry) ? 'view_list' : 'visibility';
  }

  getActionTooltip(entry: AddressBookEntry): string {
    if (this.isMultiSearch(entry)) {
      return 'Open multi search';
    }
    return this.canView(entry) ? 'View data' : this.getViewDisabledReason(entry);
  }

  canView(entry: AddressBookEntry): boolean {
    if (this.isMultiSearch(entry)) return true;
    return (
      !this.consentRequired &&
      !!entry.latestCompleteRequestId &&
      entry.dncBlocked !== true &&
      entry.viewAvailability?.mode !== 'NO_MATCH' &&
      entry.viewAvailability?.mode !== 'NOT_READY'
    );
  }

  canMarkDnc(entry: AddressBookEntry): boolean {
    if (this.isMultiSearch(entry)) return false;
    if (!entry.latestCompleteRequestId) return false;
    if (entry.dncBlocked === true) return false;
    if (entry.viewAvailability?.mode === 'NO_MATCH') return false;
    return true;
  }

  isMarkDncLoading(entry: AddressBookEntry): boolean {
    const requestId = entry.latestCompleteRequestId;
    return !!requestId && this.dncLoadingByRequestId.has(requestId);
  }

  getMarkDncTooltip(entry: AddressBookEntry): string {
    if (this.isMultiSearch(entry)) return '';
    if (entry.dncBlocked === true) return 'This contact is already marked as Do Not Contact';
    if (entry.viewAvailability?.mode === 'NO_MATCH') return 'No result found for this ID number.';
    if (!entry.latestCompleteRequestId) return 'This request can be marked once processing is complete';
    return 'Mark as Do Not Contact';
  }

  getViewDisabledReason(entry: AddressBookEntry): string {
    if (this.isMultiSearch(entry)) return '';
    if (this.consentRequired) {
      return 'Please accept the POPIA acknowledgement before viewing data.';
    }
    if (entry.viewAvailability?.mode === 'INSUFFICIENT_CREDITS') {
      return 'This request is unavailable because you had no credits when it completed.';
    }
    if (entry.viewAvailability?.mode === 'NO_MATCH') {
      return 'No result found for this ID number.';
    }
    if (entry.viewAvailability?.mode === 'FAILED') {
      return 'This request failed. Please run a new search.';
    }
    if (!entry.latestCompleteRequestId || entry.viewAvailability?.mode === 'NOT_READY') {
      return 'This request can be viewed once processing is complete';
    }
    if (entry.dncBlocked || entry.viewAvailability?.mode === 'UNAVAILABLE') {
      return 'This request is unavailable due to Do Not Contact status';
    }
    return '';
  }

  getAvailabilityIcon(entry: AddressBookEntry): string {
    if (this.isMultiSearch(entry)) return 'view_list';
    const mode = entry.viewAvailability?.mode;
    if (mode === 'NO_MATCH') return 'search_off';
    if (mode === 'FREE_UNTIL' || mode === 'FREE') return 'verified';
    if (mode === 'CREDIT_COST') return 'paid';
    if (mode === 'FAILED') return 'error';
    if (mode === 'INSUFFICIENT_CREDITS') return 'money_off';
    if (mode === 'UNAVAILABLE') return 'block';
    return 'hourglass_empty';
  }

  getAvailabilityIconColor(entry: AddressBookEntry): string {
    if (this.isMultiSearch(entry)) return '';
    const mode = entry.viewAvailability?.mode;
    if (mode === 'FREE_UNTIL' || mode === 'FREE') return 'primary';
    if (mode === 'CREDIT_COST') return 'warn';
    return '';
  }

  getAvailabilityText(entry: AddressBookEntry): string {
    if (this.isMultiSearch(entry)) return '—';
    const availability = entry.viewAvailability;
    if (!availability) return '—';
    switch (availability.mode) {
      case 'FREE':
        return 'Free view';
      case 'FREE_UNTIL':
        return `Free until ${this.formatFreeUntilLabel(availability.freeUntil || null)}`;
      case 'CREDIT_COST':
        return this.formatCreditCostLabel(availability.creditCost);
      case 'NO_MATCH':
        return 'No result found';
      case 'UNAVAILABLE':
        return 'Not available';
      case 'INSUFFICIENT_CREDITS':
        return 'Not available - no credits';
      case 'FAILED':
        return 'Failed';
      case 'NOT_READY':
      default:
        return 'Available when complete';
    }
  }

  getAvailabilityTooltip(entry: AddressBookEntry): string {
    if (this.isMultiSearch(entry)) return 'Open multi search to monitor individual requests';
    const availability = entry.viewAvailability;
    if (!availability) return 'Availability unknown';
    switch (availability.mode) {
      case 'FREE':
        return 'Viewing this entry is free';
      case 'FREE_UNTIL': {
        const full = this.formatDate(availability.freeUntil || null);
        return full ? `Free view active until ${full}` : 'Free view active';
      }
      case 'CREDIT_COST':
        return `Viewing this entry will incur a cost (${this.formatCreditCostLabel(availability.creditCost)})`;
      case 'NO_MATCH':
        return 'No result found for this ID number.';
      case 'UNAVAILABLE':
        return 'This request is unavailable due to Do Not Contact status';
      case 'INSUFFICIENT_CREDITS':
        return 'This request completed when your credit balance was zero. Buy credits and run a new search.';
      case 'FAILED':
        return 'This request failed. Please run a new search.';
      case 'NOT_READY':
      default:
        return 'This request can be viewed once processing is complete';
    }
  }

  private formatFreeUntilLabel(value: string | null): string {
    if (!value) return 'soon';
    const date = new Date(value);
    if (isNaN(date.getTime())) return 'soon';
    const time = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Johannesburg',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);

    const dayKey = this.dayKeyInJohannesburg(date);
    const now = new Date();
    const todayKey = this.dayKeyInJohannesburg(now);
    const tomorrowKey = this.dayKeyInJohannesburg(new Date(now.getTime() + 24 * 60 * 60 * 1000));
    if (dayKey === todayKey) return `${time} today`;
    if (dayKey === tomorrowKey) return `${time} tomorrow`;
    return `${time} on ${new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Johannesburg',
      day: '2-digit',
      month: '2-digit',
    }).format(date)}`;
  }

  private formatCreditCostLabel(value: number | null | undefined): string {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return 'credit cost unavailable';
    }
    const normalized = Math.max(1, Math.trunc(parsed));
    return `${normalized} credit${normalized === 1 ? '' : 's'}`;
  }

  private dayKeyInJohannesburg(date: Date): string {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Africa/Johannesburg',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  }

  async viewData(entry: AddressBookEntry): Promise<void> {
    if (this.isMultiSearch(entry)) {
      this.openMultiSearch(entry);
      return;
    }
    if (!this.canView(entry)) {
      if (this.consentRequired) {
        this.snackBar.open('Please accept the POPIA acknowledgement before viewing data', 'Close', { duration: 5000 });
        return;
      }
      if (entry.dncBlocked) {
        this.snackBar.open('This contact is unavailable due to Do Not Contact status', 'Close', { duration: 5000 });
        return;
      }
      this.snackBar.open('No completed request is available for this contact yet', 'Close', { duration: 4000 });
      return;
    }
    const canAffordView = await this.canAffordCreditCostView(entry.viewAvailability?.creditCost, entry.viewAvailability?.mode);
    if (!canAffordView) {
      this.snackBar.open('You have insufficient credits for this request.', 'Close', { duration: 5000 });
      return;
    }

    const from = this.router.url.startsWith('/user/') ? 'user-address-book' : 'address-book';
    void this.router.navigate(['/admin/data', entry.latestCompleteRequestId], {
      queryParams: { from }
    });
  }

  markAsDnc(entry: AddressBookEntry): void {
    if (!this.canMarkDnc(entry)) return;
    const requestId = entry.latestCompleteRequestId;
    if (!requestId) return;
    if (this.dncLoadingByRequestId.has(requestId)) return;

    const proceed = confirm(
      'Mark this contact as Do Not Contact (DNC)?\n\n' +
      'This will block future contact actions for this contact in your company and ' +
      'access will be restricted after confirmation.'
    );
    if (!proceed) return;

    this.dncLoadingByRequestId.add(requestId);
    this.apiService.markRequestDnc(requestId).subscribe({
      next: () => {
        this.snackBar.open(
          'Contact marked as Do Not Contact for your company. View access is now blocked',
          'Close',
          { duration: 5000 }
        );
        this.loadEntries(false);
        this.dncLoadingByRequestId.delete(requestId);
      },
      error: (error) => {
        const errorMessage = error?.error?.error?.message || 'Failed to mark contact as Do Not Contact';
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        this.dncLoadingByRequestId.delete(requestId);
      }
    });
  }

  private async canAffordCreditCostView(
    creditCost: number | null | undefined,
    mode: 'FREE' | 'FREE_UNTIL' | 'CREDIT_COST' | 'NO_MATCH' | 'FAILED' | 'NOT_READY' | 'UNAVAILABLE' | 'INSUFFICIENT_CREDITS' | undefined
  ): Promise<boolean> {
    if (mode !== 'CREDIT_COST') return true;
    const requiredCredits = Number(creditCost);
    if (!Number.isFinite(requiredCredits) || requiredCredits <= 0) return true;
    try {
      const summary = await firstValueFrom(this.apiService.getUserInfoSummary());
      const creditsRemaining = Number(summary?.credits?.creditsRemaining || 0);
      return creditsRemaining >= Math.max(1, Math.trunc(requiredCredits));
    } catch {
      // Fail open if credit summary is temporarily unavailable.
      return true;
    }
  }

  openMultiSearch(entry: AddressBookEntry): void {
    const batchId = entry.multiSearch?.requestBatchId;
    if (!batchId) return;
    const basePath = this.router.url.startsWith('/user/') ? '/user/multi-search' : '/admin/multi-search';
    const from = this.router.url.startsWith('/user/') ? 'user-address-book' : 'address-book';
    void this.router.navigate([basePath, batchId], {
      queryParams: { from }
    });
  }
}
